import React, { useMemo } from "react";
import { css } from "@linaria/core";
import Text from "@ui/Text";
import { DateTime } from "luxon";
import t from "../../../../i18n";

export const printableHiddenInline = `
    @media print {
        display: none;
    }
`;

export const printableHidden = css`
  @media print {
    display: none !important;
  }
`;

export const printablePage = css`
  :global() {
    @page {
      size: A4 landscape;
      margin: 1cm;
    }

    @media print {
      html,
      body {
        width: 100%;
      }

      html,
      body,
      #root {
        height: auto;
      }

      body {
        zoom: 1 !important;
      }

      #root > :first-child {
        display: block !important;
      }

      #root > :last-child {
        display: none !important;
      }

      /* #root * {
        border: 1px dashed black;
      }

      g {
        outline: solid 3px blue;
        outline-offset: 5px;
      } */
    }
  }

  @media print {
    display: block !important;
  }
`;

export const printableMain = css`
  @media print {
    width: 100% !important;
    padding: 0 !important;
    display: block !important;
  }
`;

export const printableGrid = css`
  @media print {
    display: block !important;
    position: relative;
  }
`;

export const printableBlockContainer = css`
  height: 100%;

  @media print {
    break-inside: avoid-page;
    margin-left: 0 !important;

    & + & {
      margin-top: 16px;
    }

    &:has(.HEADING) {
      page-break-after: avoid;
      break-after: avoid;
    }

    &:has(.TABLE) {
      display: none;
    }

    // Hide original diagrams during print (backup for JS hiding)
    .printable-diagram {
      display: none !important;
    }

    // Style the replacement images for print
    .print-image-replacement {
      display: block !important;
      width: 100% !important; // Ensure it takes full width
      height: auto !important; // Maintain aspect ratio
      break-inside: avoid !important; // Try to keep image on one page
      margin-top: 8px; // Add some space if needed
      margin-bottom: 8px; // Add some space if needed
    }
  }
`;

export const printableBlockItem = css`
  @media print {
    display: block;
    width: 100%;

    &:not(.PLOT) {
      padding-left: 0 !important;
    }
  }
`;

const printableInfoContainer = css`
  text-align: right;
  grid-column: 2 / span 1;
  grid-row: 1 / span 1;
  align-self: center;

  @media not print {
    display: none;
  }
`;

export function PrintableInfo({
  userName,
  projectName,
  projectSiteId,
  projectTypeId,
}: {
  userName?: string;
  projectName?: string;
  projectSiteId: string | undefined;
  projectTypeId: string | undefined;
}) {
  const tags = useMemo(
    () =>
      [projectSiteId, projectTypeId]
        .filter((t): t is NonNullable<string> => typeof t === "string")
        .join(", "),
    [projectSiteId, projectTypeId],
  );
  return (
    <div className={printableInfoContainer}>
      <Text type="body1SemiBold">
        {projectName} {tags.length > 0 ? `- ${tags}` : ""}
      </Text>
      <Text>
        <Text as="span" type="body1SemiBold">
          {t("project.dashboard.print.date.title")}
        </Text>{" "}
        {DateTime.now().toFormat("dd/MM/yyyy HH:mm:ss")}
      </Text>
      <Text>
        <Text as="span" type="body1SemiBold">
          {t("project.dashboard.print.export_by.title")}
        </Text>{" "}
        {userName}
      </Text>
    </div>
  );
}
